import { getCodeChallenge, getCodeVerifier } from '../../../../modules/auth/utils';
import { getBaseRedirectUrl } from '../../../../utils/base-url';
import { stringifyFullUrl } from '../../../../utils/url-params';
import { AccessToken } from '../../../access-token';
import { ApiCall } from '../../../http-service/types/ApiCall';
import { unleashClient } from '../../../UnleashProvider/const';
import { LoginUrlParams, SSOService } from '../../SSOService';
import { exchangeZitadelToken } from './api';
import { ZitadelAuthTokenExchangeParams } from './api/types/ZitadelAuthTokenExchangeParams';
import { ZitadelRefreshTokenExchangeParams } from './api/types/ZitadelRefreshTokenExchangeParams';
import { ZITADEL_BASE_URL, ZITADEL_CALLBACK_ROUTE_URL } from './const';

class ZitadelSSOAuthService implements SSOService {
  baseUrl: string;
  routeUrl: string;
  organizationId: number | null = null;

  constructor() {
    this.baseUrl = ZITADEL_BASE_URL;
    this.routeUrl = ZITADEL_CALLBACK_ROUTE_URL;
  }

  private async getBaseUrlQueryParams() {
    const baseRedirectUrl = getBaseRedirectUrl();
    const codeChallenge = await getCodeChallenge();

    const organizationScope = this.organizationId ? ` urn:zitadel:iam:org:id:${this.organizationId}` : '';
    const scope = `openid email profile offline_access${organizationScope}`;

    return {
      client_id: process.env.ZITADEL_APP_WEB_FRONTEND_CLIENT_ID!,
      response_type: 'code',
      response_mode: 'query',
      scope,
      redirect_uri: `${baseRedirectUrl}/app/sign-in/${this.getRouteUrl()}`,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    };
  }

  setOrganizationId(id: number | null) {
    this.organizationId = id;
  }

  getRouteUrl() {
    return this.routeUrl;
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  isAvailable() {
    return !!this.baseUrl && unleashClient.isEnabled('CORE-FE-OK-48752-Zitadel-SSO-Login');
  }

  public async getLoginUrl(
    params?: LoginUrlParams,
  ) {
    const baseQueryParams = await this.getBaseUrlQueryParams();
    return stringifyFullUrl({
      url: `${this.baseUrl}/oauth/v2/authorize`,
      query: {
        ...baseQueryParams,
        state: params?.state || null,
      }
    });
  }

  public getAuthTokenApiCall(code: string) {
    const baseRedirectUrl = getBaseRedirectUrl();
    const params: ZitadelAuthTokenExchangeParams = {
      client_id: process.env.ZITADEL_APP_WEB_FRONTEND_CLIENT_ID,
      grant_type: 'authorization_code',
      code_verifier: getCodeVerifier(),
      code,
      redirect_uri: `${baseRedirectUrl}/app/sign-in/${this.getRouteUrl()}`,
    };

    const apiCall: ApiCall = ({ abortSignal }) => {
      return exchangeZitadelToken({ params, abortSignal });
    };
    return apiCall;
  }

  public getRefreshTokenApiCall() {
    const params: ZitadelRefreshTokenExchangeParams = {
      client_id: process.env.ZITADEL_APP_WEB_FRONTEND_CLIENT_ID,
      grant_type: 'refresh_token',
      refresh_token: AccessToken.getRefreshToken()!,
      access_token: AccessToken.getToken()!,
      redirect_uri: `${getBaseRedirectUrl()}/app/sign-in/${this.getRouteUrl()}`
    };

    const apiCall: ApiCall = ({ abortSignal }) => {
      return exchangeZitadelToken({ params, abortSignal });
    };

    return apiCall;
  }

  public getLogoutApi(companySubdomain?: string | null) {
    return stringifyFullUrl({
      url: `${this.baseUrl}/oidc/v1/end_session`,
      query: {
        client_id: process.env.ZITADEL_APP_WEB_FRONTEND_CLIENT_ID || null,
        state: companySubdomain || null,
        post_logout_redirect_uri: `${getBaseRedirectUrl()}/app/sign-out`
      }
    });
  }
}

export const zitadelSSOService = new ZitadelSSOAuthService();